import {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {service} from '../redux/service'

interface Props {
  md5Id: string
  handleClose: () => void
}

const Field = ({label, value}: {label: string; value?: string | number | null}) => (
  <div className='col-md-4 mb-3'>
    <div className='text-muted small'>{label}</div>
    <div className='fw-semibold'>{value || '-'}</div>
  </div>
)

const SectionHeading = ({title}: {title: string}) => (
  <div className='col-12 mt-3 mb-2'>
    <h6 className='text-primary border-bottom pb-1 mb-0'>{title}</h6>
  </div>
)

const ViewSmeLoanDetails = ({md5Id, handleClose}: Props) => {
  const [application, setApplication] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    service
      .getSmeLoanApplicationDetail(md5Id)
      .then((res) => setApplication(res.data?.data))
      .catch(() => toast.error('Failed to load application details.'))
      .finally(() => setLoading(false))
  }, [md5Id])

  const bd = application?.business_detail
  const sd = application?.security_detail
  const od = application?.other_detail

  return (
    <div className='modal show d-block' style={{background: 'rgba(0,0,0,0.5)'}}>
      <div className='modal-dialog modal-xl modal-dialog-scrollable'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>
              SME Loan Application — {application?.request_code || md5Id}
            </h5>
            <button className='btn-close' onClick={handleClose} />
          </div>

          <div className='modal-body'>
            {loading ? (
              <div className='text-center py-5'>
                <div className='spinner-border text-primary' />
              </div>
            ) : !application ? (
              <div className='alert alert-danger'>Application not found.</div>
            ) : (
              <>
                {/* Header */}
                <div className='row mb-3'>
                  <Field label='Request Code' value={application.request_code} />
                  <Field label='Status' value={application.application_status} />
                  <Field label='Initiated' value={application.initiation_date_time} />
                  <Field label='Submitted' value={application.submission_date_time} />
                </div>

                {/* Business Details */}
                <div className='row'>
                  <SectionHeading title='Lead Generation' />
                  <Field label='Nature of Business' value={bd?.nature_of_business} />
                  <Field label='Contact Person' value={bd?.contact_person} />
                  <Field label='Office Name' value={bd?.office} />
                  <Field label='Official Email' value={bd?.official_email_address} />
                  <Field label='Office Number' value={bd?.office_number} />
                  <Field label='Mobile Number' value={bd?.mobile_number} />

                  <SectionHeading title='Institution Details' />
                  <Field label='Institution Name' value={bd?.institution_name} />
                  <Field label='Business Type' value={bd?.business_type} />
                  <Field label='Registration Number' value={bd?.registration_number} />
                  <Field label='Registration Date' value={bd?.registration_date} />
                  <Field label='Registered Place' value={bd?.registered_place} />
                  <Field label='Last Renewed Date' value={bd?.renewed_date} />
                  <Field label='Head Office' value={bd?.head_office} />
                  <Field label='Factory/Godown Address' value={bd?.factory_address} />
                  <Field label='PAN/VAT Number' value={bd?.pan_vat_number} />
                  <Field label='PAN Registered Date' value={bd?.pan_registered_date} />
                  <Field label='PAN Registered Place' value={bd?.pan_registered_place} />
                  <Field label='Industry License' value={bd?.industry_license} />
                  <Field label='Valid Till' value={bd?.valid_till} />
                  <Field label='Total Facility Amount' value={bd?.facility_total_amount} />

                  <SectionHeading title='Location' />
                  <Field label='Latitude' value={bd?.latitude} />
                  <Field label='Longitude' value={bd?.longitude} />
                </div>

                {/* Facility Requirements */}
                {application.facility_requirements?.length > 0 && (
                  <div className='row'>
                    <SectionHeading title='Facility Requirements' />
                    <div className='col-12'>
                      <table className='table table-bordered table-sm'>
                        <thead className='table-light'>
                          <tr>
                            <th>#</th>
                            <th>Type</th>
                            <th>Amount</th>
                            <th>Tenure (Months)</th>
                            <th>Purpose</th>
                          </tr>
                        </thead>
                        <tbody>
                          {application.facility_requirements.map((f: any, i: number) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{f.type || '-'}</td>
                              <td>{f.amount || '-'}</td>
                              <td>{f.tenure || '-'}</td>
                              <td>{f.purpose || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Owner Details */}
                {application.owner_details?.length > 0 && (
                  <div className='row'>
                    <SectionHeading title='Details of Owners' />
                    {application.owner_details.map((o: any, i: number) => (
                      <div key={i} className='col-12 border rounded p-2 mb-2'>
                        <div className='fw-bold text-muted small mb-2'>Owner {i + 1}</div>
                        <div className='row'>
                          <Field label='Full Name' value={o.proprietor} />
                          <Field label='Gender' value={o.gender} />
                          <Field label='Share Holding %' value={o.share_holding} />
                          <Field label="Father's Name" value={o.fathers_name} />
                          <Field label="Grand Father's Name" value={o.grand_fathers_name} />
                          <Field label='Spouse Name' value={o.spouse_name} />
                          <Field label='Citizenship Number' value={o.citizenship_number} />
                          <Field label='Citizenship Issued Place' value={o.citizenship_issued_place} />
                          <Field label='Citizenship Issued Date' value={o.citizenship_issued_date} />
                          <Field label='DOB (AD)' value={o.date_of_birth_ad} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Security Details */}
                <div className='row'>
                  <SectionHeading title='Security / Collateral Details' />
                  <Field label='Collateral Type' value={sd?.collateral_type} />
                  <Field label='Primary Security' value={sd?.primary_security} />
                  <Field label='Statement Date' value={sd?.statement_date} />
                  <Field label='Directors are Guarantors?' value={sd?.are_directors_guarantors ? 'Yes' : 'No'} />
                  <Field label='Other Guarantors?' value={sd?.other_guarantors ? 'Yes' : 'No'} />
                </div>

                {/* Property Details */}
                {application.property_details?.length > 0 && (
                  <div className='row'>
                    <SectionHeading title='Property Details' />
                    <div className='col-12'>
                      <table className='table table-bordered table-sm'>
                        <thead className='table-light'>
                          <tr><th>#</th><th>Kitta No.</th><th>Land Area</th><th>Province</th><th>District</th><th>Ward No.</th></tr>
                        </thead>
                        <tbody>
                          {application.property_details.map((p: any, i: number) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{p.kitta_number || '-'}</td>
                              <td>{p.land_area || '-'}</td>
                              <td>{p.province || '-'}</td>
                              <td>{p.district || '-'}</td>
                              <td>{p.ward_number || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Vehicle Details */}
                {application.vehicle_details?.length > 0 && (
                  <div className='row'>
                    <SectionHeading title='Vehicle Details' />
                    <div className='col-12'>
                      <table className='table table-bordered table-sm'>
                        <thead className='table-light'>
                          <tr><th>#</th><th>Type</th><th>Engine No.</th><th>Chassis No.</th><th>Reg. No.</th></tr>
                        </thead>
                        <tbody>
                          {application.vehicle_details.map((v: any, i: number) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{v.vehicle_type || '-'}</td>
                              <td>{v.engine_number || '-'}</td>
                              <td>{v.chassis_number || '-'}</td>
                              <td>{v.registration_number || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Share Details */}
                {application.share_details?.length > 0 && (
                  <div className='row'>
                    <SectionHeading title='Share / BOID Details' />
                    <div className='col-12'>
                      <table className='table table-bordered table-sm'>
                        <thead className='table-light'>
                          <tr><th>#</th><th>Company</th><th>BOID</th><th>No. of Shares</th><th>Market Value</th></tr>
                        </thead>
                        <tbody>
                          {application.share_details.map((s: any, i: number) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{s.company_name || '-'}</td>
                              <td>{s.boid_number || '-'}</td>
                              <td>{s.number_of_shares || '-'}</td>
                              <td>{s.market_value || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Guarantor Details */}
                {application.guarantor_details?.length > 0 && (
                  <div className='row'>
                    <SectionHeading title='Guarantor Details' />
                    {application.guarantor_details.map((g: any, i: number) => (
                      <div key={i} className='col-12 border rounded p-2 mb-2'>
                        <div className='fw-bold text-muted small mb-2'>Guarantor {i + 1}</div>
                        <div className='row'>
                          <Field label='Name' value={g.name} />
                          <Field label="Father's Name" value={g.fathers_name} />
                          <Field label='Citizenship Number' value={g.citizenship_number} />
                          <Field label='Citizenship Issued Place' value={g.citizenship_issued_place} />
                          <Field label='Citizenship Issued Date' value={g.citizenship_issued_date} />
                          <Field label='Contact Number' value={g.contact_number} />
                          <Field label='Address' value={g.address} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Other Details */}
                <div className='row'>
                  <SectionHeading title='Other Bank Facilities' />
                  <Field label='Other Bank Name' value={od?.other_bank_name} />
                  <Field label='Facility Type' value={od?.other_bank_facility_type} />
                  <Field label='Outstanding Amount (NPR)' value={od?.other_bank_outstanding} />
                  <Field label='Overdue Amount (NPR)' value={od?.other_bank_overdue} />
                  <Field label='Remarks' value={od?.remarks} />
                </div>

                {/* Major Buyers */}
                {application.major_buyer_details?.length > 0 && (
                  <div className='row'>
                    <SectionHeading title='Major Buyers' />
                    <div className='col-12'>
                      <table className='table table-bordered table-sm'>
                        <thead className='table-light'>
                          <tr><th>#</th><th>Name</th><th>Address</th><th>Contact</th><th>Annual Sales</th></tr>
                        </thead>
                        <tbody>
                          {application.major_buyer_details.map((b: any, i: number) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{b.name || '-'}</td>
                              <td>{b.address || '-'}</td>
                              <td>{b.contact || '-'}</td>
                              <td>{b.annual_sales || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Major Suppliers */}
                {application.major_supplier_details?.length > 0 && (
                  <div className='row'>
                    <SectionHeading title='Major Suppliers' />
                    <div className='col-12'>
                      <table className='table table-bordered table-sm'>
                        <thead className='table-light'>
                          <tr><th>#</th><th>Name</th><th>Address</th><th>Contact</th><th>Annual Purchase</th></tr>
                        </thead>
                        <tbody>
                          {application.major_supplier_details.map((s: any, i: number) => (
                            <tr key={i}>
                              <td>{i + 1}</td>
                              <td>{s.name || '-'}</td>
                              <td>{s.address || '-'}</td>
                              <td>{s.contact || '-'}</td>
                              <td>{s.annual_purchase || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          <div className='modal-footer'>
            <button className='btn btn-secondary btn-sm' onClick={handleClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewSmeLoanDetails
